import React, { useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MapPin, Loader2, AlertCircle, Info, Plus, Building2, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  MANUAL_STOP_LIMITS,
  manualStopSubmissionSchema,
  toFieldErrors,
  type CustomerContactInput,
  type ManualStopSubmission,
  type ManualStopSubmissionInput,
  type StopKind
} from '@/lib/validation/manualStopValidation';
import { ManualStopRpcError } from '@/lib/supabase/manualStopContacts';
import { VehicleEntryRow, type VehicleEntryHandle } from './manual-stop/VehicleEntryRow';
import { CustomerContactFields } from './manual-stop/CustomerContactFields';
import { EMPTY_CUSTOMER_CONTACT } from './manual-stop/formHelpers';
import { LocationFields } from './manual-stop/LocationFields';
import { useManualStopAddress } from './manual-stop/useManualStopAddress';
import { FieldError } from './manual-stop/FieldError';

interface ManualStopFormProps {
  onSubmit: (submission: ManualStopSubmission) => Promise<void>;
}

/** Errors under `prefix.`, prefix removed: {"contact.email": m} → {email: m}. */
const scopedErrors = (errors: Record<string, string>, prefix: string) =>
  Object.fromEntries(
    Object.entries(errors)
      .filter(([k]) => k.startsWith(prefix))
      .map(([k, v]) => [k.slice(prefix.length), v])
  );

// eslint-disable-next-line max-lines-per-function, complexity
export const ManualStopForm: React.FC<ManualStopFormProps> = ({ onSubmit }) => {
  const [stopKind, setStopKind] = useState<StopKind>('business');
  const addressState = useManualStopAddress(stopKind);
  const [contact, setContact] = useState<CustomerContactInput>(EMPTY_CUSTOMER_CONTACT);
  const [note, setNote] = useState('');

  const nextIdRef = useRef(1);
  const [entryIds, setEntryIds] = useState<string[]>(() => [`v-${Date.now()}-0`]);
  const rowRefs = useRef<Map<string, VehicleEntryHandle>>(new Map());

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleKindChange = (value: string) => {
    if (value !== 'business' && value !== 'customer') return;
    setStopKind(value);
    // Business and customer addresses are searched differently — start the location over.
    addressState.reset();
    setFieldErrors({});
    setSubmitError(null);
  };

  const handleAddVehicle = () => {
    const id = `v-${Date.now()}-${nextIdRef.current++}`;
    setEntryIds(prev => [...prev, id]);
  };

  const handleRemoveVehicle = (id: string) => {
    setEntryIds(prev => prev.filter(x => x !== id));
    rowRefs.current.delete(id);
  };

  const resetForm = useCallback(() => {
    addressState.reset();
    setContact(EMPTY_CUSTOMER_CONTACT);
    setNote('');
    rowRefs.current.clear();
    nextIdRef.current = 1;
    setEntryIds([`v-${Date.now()}-0`]);
    setSubmitError(null);
    setFieldErrors({});
  }, [addressState]);

  const buildInput = (): ManualStopSubmissionInput | null => {
    const { fields, search, unit } = addressState;
    if (!fields) return null;
    // An empty ZIP fails validation with a clear message (the server requires one).
    const location = { ...fields, postal_code: fields.postal_code ?? '', address_line2: unit };
    const vehicles = entryIds
      .map(id => rowRefs.current.get(id)?.getData())
      .filter((v): v is NonNullable<typeof v> => !!v);
    const summary = addressState.buildSummary();
    const shared = {
      dispatcher_note: note,
      vehicles,
      ...(summary && { address_validation: summary })
    };
    if (stopKind === 'customer') {
      return { stop_kind: 'customer', location, contact, ...shared };
    }
    const name = search.locationName || search.selected?.displayName.split(',')[0] || '';
    return {
      stop_kind: 'business',
      location: {
        ...location,
        name,
        ...(search.selected?.phone && { phone: search.selected.phone })
      },
      ...shared
    };
  };

  // eslint-disable-next-line complexity -- linear validate → submit flow
  const handleSubmit = async () => {
    setSubmitError(null);
    const input = buildInput();
    if (!input) {
      setFieldErrors({ 'location.address': 'Pick the address from the suggestions' });
      return;
    }

    // Component-level checks (VIN decoded by NHTSA) first, then the schema.
    const rowErrors: Record<string, string> = {};
    entryIds.forEach((id, i) => {
      const rowError = rowRefs.current.get(id)?.validate();
      if (rowError) rowErrors[`vehicles.${i}.vin`] = rowError;
    });
    const parsed = manualStopSubmissionSchema.safeParse(input);
    // Row messages ("VIN couldn't be decoded") are more specific than schema ones — they win.
    const errors = { ...(parsed.success ? {} : toFieldErrors(parsed.error)), ...rowErrors };
    if (!parsed.success || Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitError(errors['vehicles'] ?? 'Fix the highlighted fields to continue.');
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    try {
      await onSubmit(parsed.data);
      resetForm();
    } catch (err) {
      if (err instanceof ManualStopRpcError && err.formPath) {
        setFieldErrors({ [err.formPath]: err.serverMessage });
      }
      setSubmitError(err instanceof Error ? err.message : 'Failed to add manual stop');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stopCount = entryIds.length;
  const noteRemaining = MANUAL_STOP_LIMITS.note - note.length;
  const isSubmitDisabled =
    isSubmitting || !addressState.fields || addressState.isChecking || stopCount === 0;

  return (
    <TooltipProvider delayDuration={200}>
      <div className='p-4 space-y-5'>
        <div className='flex items-center gap-2'>
          <MapPin className='h-4 w-4 text-[#0ea5e9]' />
          <h3 className='text-base font-semibold'>Add Manual Stop</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                className='text-muted-foreground hover:text-foreground'
                aria-label='About manual stops'
              >
                <Info className='h-4 w-4' />
              </button>
            </TooltipTrigger>
            <TooltipContent side='bottom' className='max-w-xs'>
              Add one or more vehicles at a business or a customer&apos;s address. Each vehicle
              becomes its own entry on the map and can be assigned on the routing page.
            </TooltipContent>
          </Tooltip>
        </div>

        <Tabs value={stopKind} onValueChange={handleKindChange}>
          <TabsList className='grid w-full grid-cols-2' aria-label='Stop type'>
            <TabsTrigger value='business' className='gap-1.5'>
              <Building2 className='h-3.5 w-3.5' /> Business
            </TabsTrigger>
            <TabsTrigger value='customer' className='gap-1.5'>
              <Home className='h-3.5 w-3.5' /> Customer
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {stopKind === 'customer' && (
          <CustomerContactFields
            value={contact}
            onChange={setContact}
            errors={scopedErrors(fieldErrors, 'contact.')}
          />
        )}

        <LocationFields stopKind={stopKind} addressState={addressState} errors={fieldErrors} />

        <div className='space-y-3'>
          {entryIds.map((id, idx) => (
            <VehicleEntryRow
              key={id}
              index={idx}
              stopKind={stopKind}
              canRemove={entryIds.length > 1}
              onRemove={() => handleRemoveVehicle(id)}
              errors={scopedErrors(fieldErrors, `vehicles.${idx}.`)}
              ref={handle => {
                if (handle) rowRefs.current.set(id, handle);
                else rowRefs.current.delete(id);
              }}
            />
          ))}
          <Button
            type='button'
            variant='outline'
            onClick={handleAddVehicle}
            className='w-full gap-2'
          >
            <Plus className='h-4 w-4' />
            Add another vehicle
          </Button>
        </div>

        <div className='space-y-2'>
          <Label htmlFor='dispatcher-note'>Note for technician (optional)</Label>
          <Textarea
            id='dispatcher-note'
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={MANUAL_STOP_LIMITS.note}
            rows={3}
            placeholder='Gate code, parking, where the keys are…'
            aria-describedby='dispatcher-note-help'
            className={cn(fieldErrors['dispatcher_note'] && 'border-destructive')}
          />
          <div
            id='dispatcher-note-help'
            className='flex justify-between gap-2 text-xs text-muted-foreground'
          >
            <span>
              Shown on the printout and in the technician app. Don&apos;t include payment details.
            </span>
            <span className='shrink-0 tabular-nums'>{noteRemaining}</span>
          </div>
          <FieldError message={fieldErrors['dispatcher_note']} />
        </div>

        {submitError && (
          <div className='p-3 border border-destructive rounded-lg bg-destructive/10'>
            <p className='text-sm text-destructive flex items-start gap-2'>
              <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
              <span>{submitError}</span>
            </p>
          </div>
        )}

        <Button
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className='w-full bg-[#0ea5e9] hover:bg-[#0ea5e9]/90'
        >
          {isSubmitting ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Adding {stopCount === 1 ? 'Stop' : 'Stops'}…
            </>
          ) : (
            <>
              <MapPin className='h-4 w-4 mr-2' />
              Add {stopCount} {stopCount === 1 ? 'stop' : 'stops'} to route
            </>
          )}
        </Button>
      </div>
    </TooltipProvider>
  );
};

export default ManualStopForm;
